import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Crown, Shield, MapPin, Users, Check, Star, ArrowRight, 
  Building, Award, ChevronRight, Wallet, AlertCircle
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Chandigarh'
];

const FranchisePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [myFranchise, setMyFranchise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [formData, setFormData] = useState({
    city: '',
    state: '',
    district: ''
  });
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [packagesRes, franchiseRes] = await Promise.all([
        api.get('/franchise/packages'),
        user ? api.get('/franchise/my-franchise').catch(() => ({ data: { franchise: null } })) : Promise.resolve({ data: { franchise: null } })
      ]);
      
      setPackages(packagesRes.data.packages || []);
      setMyFranchise(franchiseRes.data.franchise);
      
      // Get wallet balance
      if (user) {
        try {
          const walletRes = await api.get('/wallets/my-wallet');
          setWalletBalance(walletRes.data?.cash_balance || 0);
        } catch (e) {
          console.error('Failed to fetch wallet');
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAvailability = async () => {
    if (!selectedPackage || !formData.city || !formData.state) {
      toast.error('Please fill in city and state');
      return;
    }

    try {
      const params = new URLSearchParams({
        franchise_type: selectedPackage.id,
        city: formData.city,
        state: formData.state
      });
      if (selectedPackage.id === 'district_captain' && formData.district) {
        params.append('district', formData.district);
      }
      
      const response = await api.get(`/franchise/check-availability?${params.toString()}`);
      setAvailability(response.data);
    } catch (error) {
      toast.error('Failed to check availability');
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!selectedPackage || !formData.city || !formData.state) {
      toast.error('Please fill all required fields');
      return;
    }

    if (walletBalance < selectedPackage.price) {
      toast.error(`Insufficient balance. Required: ₹${selectedPackage.price.toLocaleString()}`);
      return;
    }

    setPurchasing(true);
    try {
      const response = await api.post('/franchise/purchase', {
        franchise_type: selectedPackage.id,
        city: formData.city,
        state: formData.state,
        district: selectedPackage.id === 'district_captain' ? formData.district : null
      });
      
      toast.success(response.data.message);
      setMyFranchise(response.data.franchise);
      setSelectedPackage(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purchase franchise');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // If user already has a franchise, show their dashboard
  if (myFranchise) {
    return <FranchiseDashboard franchise={myFranchise} user={user} />;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="franchise-page">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-yellow-500/20 via-background to-purple-500/10 py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-yellow-500/20 px-4 py-2 rounded-full text-yellow-500 text-sm font-semibold mb-6">
            <Crown className="w-4 h-4" />
            Franchise Opportunity
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Become a <span className="text-primary">Captain</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Lead your city or district. Earn by approving new members and build your network empire.
          </p>
        </div>
      </div>

      {/* Packages */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative glass-card overflow-hidden transition-all ${
                selectedPackage?.id === pkg.id ? 'ring-2 ring-primary' : ''
              }`}
              data-testid={`franchise-package-${pkg.id}`}
            >
              {/* Header */}
              <div className={`p-6 ${
                pkg.id === 'city_captain' 
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500' 
                  : 'bg-gradient-to-r from-purple-500 to-pink-500'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {pkg.id === 'city_captain' ? (
                      <Building className="w-10 h-10 text-white" />
                    ) : (
                      <Crown className="w-10 h-10 text-white" />
                    )}
                    <div>
                      <h3 className="text-2xl font-bold text-white">{pkg.name}</h3>
                      <p className="text-white/70 text-sm">
                        {pkg.id === 'city_captain' ? 'Lead Your City' : 'Manage Your District'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white/70 text-sm">One-time</p>
                    <p className="text-3xl font-bold text-white">₹{pkg.price.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-muted-foreground mb-6">{pkg.description}</p>

                <div className="mb-6">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500" />
                    Benefits
                  </h4>
                  <ul className="space-y-2">
                    {pkg.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-500" />
                    Requirements
                  </h4>
                  <ul className="space-y-2">
                    {pkg.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => setSelectedPackage(selectedPackage?.id === pkg.id ? null : pkg)}
                  className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                    selectedPackage?.id === pkg.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-white/10 hover:bg-white/20'
                  }`}
                  data-testid={`select-${pkg.id}-btn`}
                >
                  {selectedPackage?.id === pkg.id ? 'Selected' : 'Select Package'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Purchase Form */}
        {selectedPackage && (
          <div className="mt-12 glass-card p-6 animate-in slide-in-from-bottom-4" data-testid="franchise-purchase-form">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Select Your Territory
            </h3>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">State *</label>
                <select
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  data-testid="franchise-state-select"
                >
                  <option value="">Select State</option>
                  {INDIAN_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">City *</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Enter city name"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  data-testid="franchise-city-input"
                />
              </div>
              {selectedPackage.id === 'district_captain' && (
                <div>
                  <label className="block text-sm font-medium mb-2">District</label>
                  <input
                    type="text"
                    value={formData.district}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    placeholder="Enter district name"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    data-testid="franchise-district-input"
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
              <button
                onClick={checkAvailability}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium"
              >
                Check Availability
              </button>
              
              {availability && (
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                  availability.available ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                }`}>
                  {availability.available ? (
                    <>
                      <Check className="w-4 h-4" />
                      Position Available!
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4" />
                      Position Taken
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Wallet Info */}
            <div className="p-4 bg-white/5 rounded-xl mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Wallet className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm text-muted-foreground">Your Cash Wallet</p>
                  <p className="font-bold">₹{walletBalance.toLocaleString()}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Required</p>
                <p className={`font-bold ${walletBalance >= selectedPackage.price ? 'text-green-500' : 'text-red-500'}`}>
                  ₹{selectedPackage.price.toLocaleString()}
                </p>
              </div>
            </div>

            <button
              onClick={handlePurchase}
              disabled={purchasing || walletBalance < selectedPackage.price || !formData.city || !formData.state}
              className="w-full py-4 bg-gradient-to-r from-primary to-yellow-500 text-black font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="purchase-franchise-btn"
            >
              {purchasing ? (
                <>
                  <div className="animate-spin w-5 h-5 border-2 border-black border-t-transparent rounded-full"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Crown className="w-5 h-5" />
                  Purchase {selectedPackage.name} - ₹{selectedPackage.price.toLocaleString()}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Franchise Dashboard Component
const FranchiseDashboard = ({ franchise, user }) => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [cityCaptains, setCityCaptains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const franchiseRes = await api.get('/franchise/my-franchise');
      setStats(franchiseRes.data.stats || {});
      
      // Get pending approvals
      const pendingRes = await api.get('/franchise/pending-approvals');
      setPendingUsers(pendingRes.data.pending_users || []);
      
      // If district captain, get city captains
      if (franchise.franchise_type === 'district_captain') {
        const captainsRes = await api.get('/franchise/city-captains');
        setCityCaptains(captainsRes.data.city_captains || []);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await api.post(`/franchise/approve-member/${userId}`);
      toast.success('Member approved successfully');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve member');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="franchise-dashboard">
      {/* Header */}
      <div className={`py-8 px-4 ${
        franchise.franchise_type === 'city_captain'
          ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/10'
          : 'bg-gradient-to-r from-purple-500/20 to-pink-500/10'
      }`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4">
            {franchise.franchise_type === 'city_captain' ? (
              <div className="w-16 h-16 bg-blue-500/30 rounded-2xl flex items-center justify-center">
                <Building className="w-8 h-8 text-blue-500" />
              </div>
            ) : (
              <div className="w-16 h-16 bg-purple-500/30 rounded-2xl flex items-center justify-center">
                <Crown className="w-8 h-8 text-purple-500" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold">
                {franchise.franchise_type === 'city_captain' ? 'City Captain' : 'District Captain'} Dashboard
              </h1>
              <p className="text-muted-foreground">
                {franchise.city}, {franchise.state}
                {franchise.district && ` - ${franchise.district}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-4 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 text-blue-500" />
            <p className="text-2xl font-bold">{franchise.members_approved}</p>
            <p className="text-sm text-muted-foreground">Members Approved</p>
          </div>
          <div className="glass-card p-4 text-center">
            <Wallet className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold">₹{franchise.total_earnings?.toLocaleString() || 0}</p>
            <p className="text-sm text-muted-foreground">Total Earnings</p>
          </div>
          <div className="glass-card p-4 text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
            <p className="text-2xl font-bold">{stats.pending_approvals || pendingUsers.length}</p>
            <p className="text-sm text-muted-foreground">Pending Approvals</p>
          </div>
          {franchise.franchise_type === 'district_captain' && (
            <div className="glass-card p-4 text-center">
              <Building className="w-8 h-8 mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold">{stats.city_captains_count || cityCaptains.length}</p>
              <p className="text-sm text-muted-foreground">City Captains</p>
            </div>
          )}
        </div>

        {/* Pending Approvals */}
        <div className="glass-card mb-8">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="font-bold flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Pending Member Approvals
            </h2>
            <span className="px-3 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-sm">
              {pendingUsers.length} pending
            </span>
          </div>
          
          {pendingUsers.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Check className="w-12 h-12 mx-auto mb-4 text-green-500" />
              <p>No pending approvals</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {pendingUsers.map((pendingUser) => (
                <div key={pendingUser.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium">{pendingUser.name}</p>
                      <p className="text-sm text-muted-foreground">{pendingUser.email}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {pendingUser.city}, {pendingUser.state}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleApprove(pendingUser.id)}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 flex items-center gap-2"
                    data-testid={`approve-user-${pendingUser.id}`}
                  >
                    <Check className="w-4 h-4" />
                    Approve
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* City Captains (for District Captain) */}
        {franchise.franchise_type === 'district_captain' && (
          <div className="glass-card">
            <div className="p-4 border-b border-white/10">
              <h2 className="font-bold flex items-center gap-2">
                <Building className="w-5 h-5 text-primary" />
                City Captains in Your District
              </h2>
            </div>
            
            {cityCaptains.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Building className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No city captains in your district yet</p>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {cityCaptains.map((captain) => (
                  <div key={captain.id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                        <Building className="w-6 h-6 text-blue-500" />
                      </div>
                      <div>
                        <p className="font-medium">{captain.user?.name || 'Unknown'}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {captain.city}, {captain.state}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {captain.members_approved} members approved
                        </p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm ${
                      captain.status === 'active' 
                        ? 'bg-green-500/20 text-green-500' 
                        : 'bg-red-500/20 text-red-500'
                    }`}>
                      {captain.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FranchisePage;
